/**
 * /admin/courses — every course, draft or published. A new course starts as a
 * draft; coaches see it once it is published.
 *
 * Aliases: courses admin, course list admin, business builder course admin
 */

import { useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { GraduationCap, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useAppForm } from "~/components/form/app-form";
import {
  FieldErrorsProvider,
  zodErrorLookup,
} from "~/components/form/fields/field-errors";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { EmptyState } from "~/components/ui/empty-state";
import {
  coursesQueryOptions,
  createCourseFn,
  learningAdminKeys,
} from "~/lib/functions/admin-learning";
import { courseInputSchema } from "~/lib/shared/learning/admin-inputs";
import { generateSlug } from "~/lib/shared/slug";

import { CourseStatusBadge } from "./-components/course-status-badge";

export const Route = createFileRoute("/admin/courses/")({
  staticData: { title: "Courses" },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(coursesQueryOptions());
  },
  component: CoursesPage,
});

function CoursesPage() {
  const { data: courses = [] } = useQuery(coursesQueryOptions());
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-medium text-gray-900">Courses</h1>
          <p className="text-text-secondary max-w-2xl text-sm text-pretty">
            Each published course is its own item in the coach menu. A new
            version of a course is a new course, so coaches mid-way through the
            old one keep their progress.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} variant="primary">
          <Plus aria-hidden="true" className="size-4" />
          New course
        </Button>
      </div>

      {courses.length === 0 ? (
        <EmptyState
          description="Create the Business Builder Course to start adding weeks."
          icon={GraduationCap}
          title="No courses yet"
        />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
          {courses.map((course) => (
            <li key={course.id}>
              <Link
                className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-gray-50"
                params={{ courseId: course.id }}
                to="/admin/courses/$courseId"
              >
                <span className="min-w-0 flex-1 font-medium text-gray-900">
                  {course.title}
                </span>
                <span className="text-text-tertiary text-xs">
                  /{course.slug} ·{" "}
                  {course.weekCount === 1
                    ? "1 week"
                    : `${course.weekCount} weeks`}
                </span>
                <CourseStatusBadge status={course.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {creating && <NewCourseDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function NewCourseDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [takenSlug, setTakenSlug] = useState<string | null>(null);

  const form = useAppForm({
    defaultValues: { title: "", slug: "", headline: "", description: "" },
  });
  const values = useStore(form.store, (state) => state.values);
  // Left empty, the address follows the name.
  const slug = values.slug.trim() || generateSlug(values.title);
  const parsed = courseInputSchema.safeParse({ ...values, slug });
  const lookup = zodErrorLookup(
    parsed.success ? [] : parsed.error.issues,
    submitAttempted,
  );
  const errorFor = (name: string): string | undefined =>
    name === "slug" && takenSlug === slug
      ? "Another course already uses this address."
      : lookup(name);

  const create = useMutation({
    mutationFn: createCourseFn,
    onSuccess: async (result, { data }) => {
      if (!result.saved) {
        setTakenSlug(data.slug);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: learningAdminKeys.all });
      toast.success("Course created as a draft.");
      await navigate({
        to: "/admin/courses/$courseId",
        params: { courseId: result.id },
      });
    },
    onError: () => toast.error("Couldn't create the course."),
  });

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      open
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-medium">New course</DialogTitle>
          <DialogDescription>
            It starts as a draft. Add its weeks, then publish it.
          </DialogDescription>
        </DialogHeader>
        <FieldErrorsProvider value={errorFor}>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              setSubmitAttempted(true);
              if (parsed.success) create.mutate({ data: parsed.data });
            }}
          >
            <form.AppField name="title">
              {(field) => (
                <field.TextField
                  helpText="The menu label, like “Business Builder Course”."
                  label="Name"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="slug">
              {(field) => (
                <field.TextField
                  helpText={`Coaches find it at /my-coaching/business/courses/${slug || "…"}`}
                  label="Address"
                  placeholder={generateSlug(values.title) || "business-builder"}
                />
              )}
            </form.AppField>
            <form.AppField name="headline">
              {(field) => (
                <field.TextField
                  helpText="The page's heading, like “Build your Client Acquisition System”."
                  label="Headline"
                  required
                />
              )}
            </form.AppField>
            <DialogFooter>
              <Button onClick={onClose} type="button" variant="ghost">
                Cancel
              </Button>
              <Button
                loading={create.isPending}
                type="submit"
                variant="primary"
              >
                Create draft
              </Button>
            </DialogFooter>
          </form>
        </FieldErrorsProvider>
      </DialogContent>
    </Dialog>
  );
}
